/**
 * Однократно вызывает `callback`, когда элемент впервые попадает в зону видимости.
 * Использует скан getBoundingClientRect (надёжнее IntersectionObserver при печати/превью).
 *
 * @param el - наблюдаемый элемент (или null — тогда no-op)
 * @param callback - вызывается один раз при входе в кадр
 * @returns функция очистки слушателей
 * @example
 * const stop = onEnter(barEl, () => { barEl.style.width = '85%' })
 * onUnmounted(stop)
 */
export function onEnter(el: HTMLElement | null, callback: () => void): () => void {
  if (el === null) return () => {}

  let done = false
  let raf = 0

  const check = (): void => {
    if (done) return
    const vh = window.innerHeight || document.documentElement.clientHeight
    const rect = el.getBoundingClientRect()
    if (rect.top < vh * 0.85 && rect.bottom > 0) {
      done = true
      cleanup()
      callback()
    }
  }

  const onScroll = (): void => {
    cancelAnimationFrame(raf)
    raf = requestAnimationFrame(check)
  }

  const cleanup = (): void => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    cancelAnimationFrame(raf)
  }

  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll)
  const timer = window.setTimeout(check, 120)
  check()

  return () => {
    cleanup()
    clearTimeout(timer)
  }
}

/** true, если пользователь предпочитает уменьшенную анимацию. */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
